import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  styled,
  SliderProps,
  Avatar,
  useTheme
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import RegularInput from '../../../../components/input/RegularInput';
import api from '../../../../api';
import { formatDateTime, sortByDay } from '../../../../utils/general';
import { dayFullDateParser } from '../../../../utils/widgetHelper';
import useNotificationsStore from '../../../../store/zustand/notifications';

const MsgContainer = styled(Box)<SliderProps>(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.primary[100]}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2)
}));

interface Props {
  // can be the proposal id or the token
  clientView?: boolean;
  entity?: string;
  id: string;
  messages: Array<any>;
  notesView?: boolean;
  updateMessages: (values: any) => void;
}

const Chat: React.FC<Props> = function Chat({
  id,
  messages,
  clientView,
  updateMessages,
  notesView,
  entity
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { addNotification } = useNotificationsStore((state) => state);
  const [postMessageApi, setPostMessageApi] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const messageRef = useRef<any>();
  let postAction;
  if (notesView) {
    postAction = api.NotesApi.postNote;
  } else if (clientView) {
    postAction = api.ProposalApi.postClientMessage;
  } else {
    postAction = api.ProposalApi.postOperatorMessage;
  }
  const postObject = notesView
    ? { entity, entityId: id, noteText: message, url: 'postNote' }
    : { id, message, url: 'postMessage' };

  const {
    data: postMessageData,
    mutate: postMessageMutate,
    error: postMessageError
  } = useSWR(postMessageApi ? postObject : null, postAction);

  const clearApiResponse = () => {
    postMessageMutate(undefined, true);
  };

  // We only can reset the component content disabled enable it
  const inputBox = () => {
    return (
      <Box alignItems="center" display="flex" gap={2}>
        <Box display="flex" justifyContent="center" width="100%">
          {postMessageApi && (
            <RegularInput
              disabled
              customStyle={{
                backgroundColor: `${theme.palette.background.paper} !important`,
                border: `1px solid ${theme.palette.secondary[100]}`,
                height: '46px',
                margin: 0,
                width: '100%'
              }}
            />
          )}
          {!postMessageApi && (
            <RegularInput
              customStyle={{
                backgroundColor: `${theme.palette.background.paper} !important`,
                border: `1px solid ${theme.palette.secondary[100]}`,
                height: '46px',
                margin: 0,
                width: '100%'
              }}
              onChange={(value: any) => {
                setMessage(value);
              }}
              value={message}
            />
          )}
        </Box>
        <Button
          onClick={() => {
            if (!message || message === '') {
              addNotification(t('NOTIFICATION.TOAST.messageEmpty'), 'error');
            } else {
              clearApiResponse();
              setPostMessageApi(true);
            }
          }}
          sx={{ width: isMobile ? 50 : 150 }}
          variant="primary"
        >
          <Typography
            sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
            variant="primary-bold"
          >
            {isMobile ? (
              <SendIcon fontSize="small" />
            ) : (
              t('LEAD.CREATE.PROPOSAL.CHAT.button')
            )}
          </Typography>
        </Button>
      </Box>
    );
  };

  const messageItem = (
    name: string,
    date: string,
    text: string,
    sendByType: string
  ) => {
    const order = sendByType === 'operator' ? 0 : 1;
    return (
      <MsgContainer>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          width="100%"
        >
          <Box
            alignItems="center"
            display="flex"
            gap={2}
            order={order}
            width="100%"
          >
            <Box alignItems="center" display="flex" gap={2} order={order}>
              <Avatar />
            </Box>
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              width="100%"
            >
              {!notesView && (
                <Typography color="primary" order={order} variant="heading_300">
                  {name}
                </Typography>
              )}
              <Typography color="secondary" variant="body_200">
                {date}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box mt={2} textAlign={order ? 'right' : 'left'} width="100%">
          <Typography variant="body_200">{text}</Typography>
        </Box>
      </MsgContainer>
    );
  };

  const getMessages = () => {
    const getMessagesParsed = sortByDay(messages, 'sentAt');
    const keys = Object.keys(getMessagesParsed);
    return keys.map((date: string) => (
      <Box
        key={date}
        marginBottom={1}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }} width="100%">
          <Typography color="secondary" variant="secondary">
            {dayFullDateParser(date)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}
        >
          {getMessagesParsed?.[date].map((item: any) => {
            const name = item?.operator
              ? `${item?.operator?.firstName} ${item?.operator?.lastName}`
              : `${item?.client?.firstName} ${item?.client?.lastName}`;
            const message = notesView ? item?.noteText : item?.message;
            return messageItem(
              name,
              formatDateTime(item?.sentAt),
              message,
              item?.sendByType
            );
          })}
        </Box>
      </Box>
    ));
  };

  useEffect(() => {
    if (postMessageData) {
      setMessage('');
      setPostMessageApi(false);
      updateMessages(postMessageData);
      addNotification(t('NOTIFICATION.TOAST.messageSent'), 'success');
    }
  }, [postMessageData]);

  useEffect(() => {
    if (postMessageError) {
      setPostMessageApi(false);
      addNotification(t('NOTIFICATION.TOAST.messageFail'), 'error');
    }
  }, [postMessageError]);

  useEffect(() => {
    setTimeout(() => {
      if (messageRef?.current) {
        messageRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        });
      }
    }, 100);
  }, [messages]);

  const getTitle = () => {
    if (notesView) {
      return t('NOTES.title');
    }
    if (clientView) {
      return t('LEAD.CREATE.PROPOSAL.CHAT.clientTitle');
    }
    return t('LEAD.CREATE.PROPOSAL.CHAT.operatorTitle');
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={3}
      height="100%"
      padding={2}
    >
      <Typography variant="heading_500">{getTitle()}</Typography>

      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        overflow="auto"
        paddingRight={1}
      >
        {messages?.length > 0 && getMessages()}
        <Box ref={messageRef} />
      </Box>
      <Box>{inputBox()}</Box>
    </Box>
  );
};

Chat.defaultProps = {
  clientView: false,
  entity: '',
  notesView: false
};

export default Chat;
